'use client'

import { C, Callout, Exercise, H, Lesson, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/ml-extra'

export default function ProjectHousePrices() {
  return (
    <Lesson>
      <Step>
        <H>Project: price a house</H>
        <P>
          Time to work like a machine-learning engineer: take a dataset and run a disciplined process from first baseline to a
          final, honest score. 300 houses, four features (size, bedrooms, age, distance to the centre), price in thousands.
        </P>
        <OL>
          <li>Split the data three ways, define the score, and build a baseline.</li>
          <li>Fit linear models, and engineer a better feature.</li>
          <li>Compare with kNN, choose using validation data, then report the test score — once.</li>
        </OL>
        <Callout kind="warn">
          The golden rule: every decision (which features, which model, which k) is made using the <strong>validation</strong>{' '}
          set. The <strong>test</strong> set is opened exactly once, at the end. Peek at it while choosing, and your final score
          is no longer an honest estimate.
        </Callout>
      </Step>

      <Step gate="house-baseline">
        <H>Part 1 · A score and a baseline</H>
        <P>
          Before building anything clever, find out how well a dumb model does. If your fancy model can’t beat “always guess
          the average price”, something’s wrong. Score models by <strong>RMSE</strong>, the root mean squared error: roughly
          the typical size of a mistake, in the same units as the price.
        </P>
        <Exercise
          id="house-baseline"
          title="rmse and baseline"
          {...EX.houseBaseline}
          hints={[
            <>
              <C>{'Math.sqrt(vec.mean(set.X.map((x, i) => (predict(x) - set.y[i]) ** 2)))'}</C>
            </>,
            <>
              <C>{'const baseline = (x: number[]) => vec.mean(trainSet.y)'}</C>
            </>,
          ]}
        />
      </Step>

      <Step gate="house-linear">
        <H>Part 2 · Linear, then better features</H>
        <P>
          A linear model is the next step. <C>fitLinear</C> is provided, and you choose what features it sees. The scatter plot
          from Part 1 showed price dropping fast near the centre and then levelling off. A straight line in km can’t bend like
          that, but you can hand the model a feature that already bends: the logarithm of the distance.
        </P>
        <Exercise
          id="house-linear"
          title="feature engineering"
          {...EX.houseLinear}
          hints={[
            <>
              <C>{'fitLinear(x => [...x, Math.log(1 + x[3])])'}</C> — the spread keeps the four original features and adds a
              fifth.
            </>,
          ]}
        />
        <Callout kind="plain">
          This is <strong>feature engineering</strong>: using what you know about the problem to give a simple model better
          inputs. Deep learning’s big promise is learning these features automatically, which is exactly what the hidden
          layers in Module 7 do.
        </Callout>
      </Step>

      <Step gate="house-final">
        <H>Part 3 · Choose, then test once</H>
        <P>
          Add a k-nearest-neighbour regressor, which averages the prices of the most similar houses. Compare all the candidates
          on validation data, pick the winner, and only then look at the test set.
        </P>
        <Exercise
          id="house-final"
          title="knnRegressor and model selection"
          {...EX.houseFinal}
          hints={[
            <>
              Inside <C>knnRegressor</C>: <C>{'const z = standardizer(trainSet.X)'}</C> and <C>{'const Z = trainSet.X.map(z)'}</C>.
              Return a function that standardises <C>x</C>, finds the k closest rows of <C>Z</C>, and averages their{' '}
              <C>trainSet.y</C>.
            </>,
            <>
              <C>{'candidates.reduce((a, b) => (rmse(b.predict, valSet) < rmse(a.predict, valSet) ? b : a))'}</C>
            </>,
          ]}
        />
        <Callout kind="why">
          The simple linear model with one well-chosen feature beats kNN by a mile here. Fancier isn’t automatically better:
          with 200 examples and a mostly linear pattern, the simple model generalises best. Always start simple, and let the
          validation data tell you when you need more.
        </Callout>
      </Step>

      <Step>
        <H>Stretch goals</H>
        <UL>
          <li>Add more engineered features (size per bedroom? age squared?). Do they help on validation, or just on training?</li>
          <li>
            Plot the validation errors of the linear model as you remove one feature at a time. Which feature matters most?
          </li>
          <li>
            Build a tiny random forest: 25 regression trees, each trained on a random resample of the training set, averaging
            their predictions. How does it compare?
          </li>
        </UL>
      </Step>
    </Lesson>
  )
}
